import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Button, Rating, TextField, Typography } from '@mui/material';
import { AiOutlineStar } from 'react-icons/ai';

/**
 * Avaliação da coleta pelo cliente, de 1 a 5 estrelas.
 *
 * As regras (prazo de 7 dias, coleta realizada, envio único) são do backend —
 * chegam prontas em `estado.podeAvaliar` / `estado.motivo`. Esta tela só
 * respeita o que veio e impede o envio de algo inválido; duplicar as regras
 * aqui criaria uma segunda versão da verdade que divergiria na primeira
 * mudança.
 */
const TEXTO_MOTIVO = {
  ja_avaliada: 'Esta coleta já foi avaliada. Obrigado pelo retorno!',
  fora_do_prazo:
    'O prazo de 7 dias para avaliar esta coleta já passou.',
  nao_realizada:
    'Esta coleta não foi realizada, então não há equipe a avaliar.',
};

const VERDE = 'var(--main-bg-color)';

function AvaliarColeta({ mostrar, handleClose, coleta, estado, onEnviar }) {
  const [nota, setNota] = useState(0);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);

  const podeAvaliar = estado?.podeAvaliar === true;
  const jaAvaliada = estado?.motivo === 'ja_avaliada';

  const enviar = async () => {
    if (!nota || enviando) return;
    setEnviando(true);
    try {
      await onEnviar({ nota, comentario });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal show={mostrar} onHide={handleClose} centered>
      <Modal.Header closeButton style={{ paddingTop: 10, paddingBottom: 10 }}>
        <Modal.Title
          style={{
            width: '100%',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            fontSize: 20,
          }}
        >
          <AiOutlineStar size={24} color={VERDE} />
          Avaliar coleta {coleta?.id}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div style={{ margin: '10px 20px', textAlign: 'center' }}>
          <Typography variant="body2" style={{ color: '#666' }}>
            Coleta de {coleta?.data}
          </Typography>

          {jaAvaliada ? (
            <>
              <Rating value={estado?.nota ?? 0} readOnly size="large" />
              <Typography variant="body2" style={{ marginTop: 8 }}>
                {TEXTO_MOTIVO.ja_avaliada}
              </Typography>
              {estado?.comentario && (
                <div
                  style={{
                    background: 'var(--main-secondy25)',
                    borderRadius: 8,
                    padding: '10px 14px',
                    marginTop: 12,
                    textAlign: 'left',
                    fontSize: 14,
                  }}
                >
                  {estado.comentario}
                </div>
              )}
            </>
          ) : !podeAvaliar ? (
            <Typography variant="body2" style={{ marginTop: 12 }}>
              {TEXTO_MOTIVO[estado?.motivo] ??
                'Esta coleta não pode ser avaliada.'}
            </Typography>
          ) : (
            <>
              <Typography variant="body1" style={{ marginTop: 8 }}>
                Como foi nossa coleta?
              </Typography>

              {/*
                getLabelText traduzido: o padrão do MUI é "2 Stars", em inglês,
                e é esse texto que o leitor de tela anuncia.
              */}
              <Rating
                name="nota"
                value={nota}
                size="large"
                getLabelText={valor =>
                  `${valor} ${valor === 1 ? 'estrela' : 'estrelas'}`
                }
                // O MUI emite `null` ao limpar e `NaN` quando o clique cai no
                // radio vazio que ele renderiza para isso — `?? 0` sozinho
                // deixaria o NaN passar e travar o botão num estado esquisito.
                onChange={(_evento, valor) =>
                  setNota(Number.isInteger(valor) ? valor : 0)
                }
                style={{ marginTop: 4 }}
              />

              <TextField
                label="Comentário (opcional)"
                value={comentario}
                onChange={evento => setComentario(evento.target.value)}
                multiline
                minRows={3}
                fullWidth
                size="small"
                inputProps={{ maxLength: 500 }}
                style={{ marginTop: 16 }}
              />
            </>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer style={{ justifyContent: 'center', gap: 10 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          style={{ borderRadius: 25, color: '#a3a3a3', borderColor: '#a3a3a3' }}
        >
          Fechar
        </Button>

        {podeAvaliar && (
          <Button
            variant="contained"
            onClick={enviar}
            disabled={!nota || enviando}
            style={{
              borderRadius: 25,
              backgroundColor: !nota || enviando ? '#a3a3a3' : VERDE,
            }}
          >
            {enviando ? 'Enviando...' : 'Enviar avaliação'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default AvaliarColeta;
